import { useState, useEffect, useCallback } from 'react';
import { MicrosoftTemplate, CustomTemplate, FlowType } from 'types/template';
import SelectTemplateFlow from 'components/TemplateWizard/SelectTemplateFlow';
import SelectExistingTemplates from 'components/TemplateWizard/ExistingTemplates/SelectExistingTemplates';
import LaunchTemplate from 'components/TemplateWizard/ExistingTemplates/LaunchTemplate';
import ConfigureTeam from 'components/TemplateWizard/NewTeam/ConfigureTeam';
import WizardSidebar from '../Wizard/WizardSidebar';
import WizardContent from '../Wizard/WizardContent';
import FullScreenWizardWrapper from '../Wizard/FullScreenWizardWrapper';
import AddChannels from './NewTeam/Channels/AddChannels';
import { Channel } from 'types/channels';
import LaunchCustomTemplate from './NewTeam/LaunchCustomTemplate';
import { WorkspaceType } from 'types/workspace';
import { useTemplateGeneration } from 'hooks/useTemplateGeneration';
import { useWizardSteps } from './useWizardSteps';
import { isMicrosoftTemplate } from 'utils/templateGuards';

type Props = {
  workspaceType: WorkspaceType;
  workspaceId: string;
  tenantId: string;
  setShowSelectTeamsTemplateDialog: (show: boolean) => void;
};

export default function TemplateWizard({
  workspaceType,
  workspaceId,
  tenantId,
  setShowSelectTeamsTemplateDialog,
}: Props) {
  const [selectedFlow, setSelectedFlow] = useState<FlowType>('new');
  const [showHelperText, setShowHelperText] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<
    MicrosoftTemplate | CustomTemplate | null
  >(null);

  const { handleOnSave, handleOnLaunch } = useTemplateGeneration(
    workspaceType,
    workspaceId,
    tenantId,
  );

  const { steps, activeStep, setActiveStep, incrementStepTransition } =
    useWizardSteps(selectedFlow);

  useEffect(() => {
    setSelectedTemplate(null);
  }, [selectedFlow]);

  const handleStepTransition = () => {
    incrementStepTransition(activeStep);
  };

  const handleTemplateSelect = (
    template: MicrosoftTemplate | CustomTemplate
  ) => {
    if (template) {
      setSelectedTemplate(template);
    }
    incrementStepTransition(activeStep);
  };

  const handleAddChannels = useCallback((channels: Channel[]) => {
    if (channels && selectedTemplate) {
      setSelectedTemplate({
        ...selectedTemplate,
        channels,
      });
    }
  },  [setSelectedTemplate]);

  const handleFinishAddChannels = useCallback((channels: Channel[]) => {
    if (selectedTemplate) {
      setSelectedTemplate({
        ...selectedTemplate,
        channels,
      });
    }
    incrementStepTransition(activeStep);
  }, [selectedTemplate, setSelectedTemplate, incrementStepTransition]);

  const handleCloseModal = () => {
    setShowSelectTeamsTemplateDialog(false);
    setSelectedFlow('new');
  };

  const handleTemplateSave = () => {
    if (selectedTemplate) {
      handleOnSave(selectedTemplate);
      handleCloseModal();
    }
  };

  const handleTemplateLaunch = () => {
    if (selectedTemplate) {
      handleOnLaunch(selectedTemplate);
      handleCloseModal();
    }
  };

  const currentStepIndex = steps.findIndex((step) => step.id === activeStep.id);

  const handleBackStep = () => {
    if (currentStepIndex > 0) {
      setActiveStep(steps[currentStepIndex - 1]);
    }
  };

  const handleDismissHelperText = () => setShowHelperText(false);

  const renderExistingFlowStep = () => {
    switch (activeStep.id) {
      case 1:
        return (
          <SelectTemplateFlow
            onModalClose={handleCloseModal}
            handleFlowSelect={(flow: FlowType) => setSelectedFlow(flow)}
            handleStepTransition={handleStepTransition}
            selectedFlow={selectedFlow}
          />
        );
      case 2:
        return (
          <SelectExistingTemplates
            handleTemplateSelect={handleTemplateSelect}
          />
        );
      case 3:
        if (!selectedTemplate) {
          return <div>No template selected. Please contact support.</div>;
        }
        return (
          <LaunchTemplate
            selectedTemplate={
              selectedTemplate as MicrosoftTemplate | CustomTemplate
            }
            onModalClose={handleCloseModal}
            onClick={handleTemplateLaunch}
          />
        );
      default:
        return null;
    }
  };

  const renderNewFlowStep = () => {
    switch (activeStep.id) {
      case 1:
        return (
          <SelectTemplateFlow
            onModalClose={handleCloseModal}
            handleFlowSelect={(flow: FlowType) => setSelectedFlow(flow)}
            handleStepTransition={handleStepTransition}
            selectedFlow={selectedFlow}
          />
        );
      case 2:
        return (
          <ConfigureTeam
            onModalClose={handleCloseModal}
            handleTeamTemplateCreation={handleTemplateSelect}
            selectedTemplate={selectedTemplate as CustomTemplate}
          />
        );
      case 3:
        return (
          <AddChannels
            onModalClose={handleCloseModal}
            handleAddChannels={handleAddChannels}
            handleFinishAddChannels={handleFinishAddChannels}
            selectedTemplate={selectedTemplate as CustomTemplate}
          />
        );
      case 4:
        if (selectedTemplate && isMicrosoftTemplate(selectedTemplate)) {
          return (
            <div>
              There was an error with your template. Please contact support.
            </div>
          );
        } else if (!selectedTemplate) {
          return <div>No template selected. Please contact support.</div>;
        }
        return (
          <LaunchCustomTemplate
            selectedTemplate={selectedTemplate as CustomTemplate}
            onModalClose={handleCloseModal}
            onLaunch={handleTemplateLaunch}
            onSave={handleTemplateSave}
          />
        );
      default:
        return null;
    }
  };

  const renderStepComponent = () =>
    selectedFlow === 'existing'
      ? renderExistingFlowStep()
      : renderNewFlowStep();

  return (
    <FullScreenWizardWrapper
      title="Provisions Teams Site"
      handleCloseModal={handleCloseModal}
    >
      <WizardSidebar
        steps={steps}
        activeStep={activeStep}
        showHelperText={showHelperText}
        handleDismissHelperText={handleDismissHelperText}
        helperText={`Create new or enhance existing Teams templates through PRATUS
            for improved insights and productivity.`}
      />
      <WizardContent
        steps={steps}
        activeStep={activeStep}
        currentStepIndex={currentStepIndex}
        renderStepComponent={renderStepComponent}
        handleBackStep={handleBackStep}
      />
    </FullScreenWizardWrapper>
  );
}
